import * as React from 'react';
import { useMemo } from 'react';
import { Label } from '@frontdoor/ui/components/label';
import { cn } from '@frontdoor/ui/lib/utils';
import { type VariantProps, cva } from 'class-variance-authority';

const fieldVariants = cva('group/field flex w-full gap-2', {
  variants: {
    orientation: {
      none: [],
      vertical: ['flex-col [&>*]:w-full [&>.sr-only]:w-auto'],
      floating: ['flex-col w-full [&>*]:w-auto [&>.sr-only]:w-auto relative'],
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
});

function Field({
  className,
  orientation = 'floating',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof fieldVariants>) {
  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  );
}

function FieldLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
  return (
    <Label
      data-slot="field-label"
      className={cn(
        'flex w-fit gap-2 group-data-[disabled=true]/field:opacity-50',
        'text-base font-normal text-main group-data-[invalid=true]/field:text-destructive',
        'group-data-[orientation=floating]/field:absolute group-data-[orientation=floating]/field:top-0 group-data-[orientation=floating]/field:left-3 group-data-[orientation=floating]/field:z-10 group-data-[orientation=floating]/field:-translate-y-1/2 group-data-[orientation=floating]/field:bg-jasmine-100 group-data-[orientation=floating]/field:px-1',
        'group-data-[orientation=floating]/field:group-has-[[data-slot=input]:focus-visible]/field:group-data-[invalid=false]/field:text-hydrangea-500',
        className
      )}
      {...props}
    />
  );
}

function FieldDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="field-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

function FieldError({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<'div'> & {
  errors?: Array<{ message?: string } | undefined>;
}) {
  const content = useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors?.length) {
      return null;
    }

    const uniqueErrors = [...new Map(errors.map((error) => [error?.message, error])).values()];

    if (uniqueErrors.length === 1) {
      return uniqueErrors[0]?.message;
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {uniqueErrors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn('text-sm font-normal text-destructive italic', className)}
      {...props}
    >
      {content}
    </div>
  );
}

export { Field, FieldLabel, FieldDescription, FieldError };
